import { useCallback, useContext, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import "react-toastify/dist/ReactToastify.min.css";
import { toast, ToastContainer } from "react-toastify";

import ActionButton from "./ActionButton";
import BreadCrumb from "./BreadCrumb";
import ClientDao from "../dao/ClientDao";
import FacetedSearch from "./facetedSearch/FacetedSearch";
import FinTransDao from "../dao/FinTransDao";
import Header from "./header/Header";
import LoadingSvg2 from "./LoadingSvg2";
import PaymentsTable from "./PaymentsTable";
import QuarterSelect from "./QuarterSelect";
import RecaptchaDao from "../dao/RecaptchaDao";
import SchoolYearSelect from "./SchoolYearSelect";
import T from "./translations/Translations";

import { CAPTCHA_SITE_KEY } from "../utils/auth/config";
import { GlobalContext } from "./ContextProvider";
import { SpendCategories } from "../const/SpendCategories";

import "../styles/global/Global.scss";
import "../styles/FacetedSearch.scss";

/**
 * Financial Transparency Main
 * @name Main
 * @constructor
 * @return {JSX.Element}
 */
const Main = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const { schoolYearQuarters, token } = state;

    const pageNumRef = useRef(1);
    const recaptchaRef = useRef(null);
    const timerRef = useRef(null);

    const [activeQuarters, setActiveQuarters] = useState(null);
    const [currentQuarter, setCurrentQuarter] = useState(null);
    const [displayForm, setDisplayForm] = useState(false);
    const [facetFields, setFacetFields] = useState(null);
    const [facetPagination, setFacetPagination] = useState(null);
    const [googleResponse, setGoogleResponse] = useState(null);
    const [loader, setLoader] = useState(false);
    const [pageNum, setPageNum] = useState(1);
    const [params, setParams] = useState({});
    const [payments, setPayments] = useState(null);
    const [reportableSchoolYears, setReportableSchoolYears] = useState(null);
    const [searchString, setSearchString] = useState("");
    const [selectedFacets, setSelectedFacets] = useState([]);
    const [selectedQuarter, setSelectedQuarter] = useState(null);
    const [selectedSchoolYear, setSelectedSchoolYear] = useState(null);
    const [userHeaders, setUserHeaders] = useState(null);

    // these values will not change
    const facetField = "fundName,costCenterName,paymentType,spendCategoryName";
    const facetLimit = 100;
    const numRows = 50;
    const sort = "vendorName:asc,fundName:asc,costCenterName:asc,paymentAmount:asc,paymentDateDisplay:asc";
    const timeInMilliseconds = 1800000; // 30 minutes

    /**
     * Whenever a fiscal year or selectedQuarter is changed, reset search params
     * @name resetSearch
     * @param {bool} changed
     */
    const resetSearch = (changed = false) => {
        pageNumRef.current = 1;
        setFacetFields(null);
        setPageNum(1);
        setParams({});
        setSelectedFacets([]);
        if (!changed) {
            setActiveQuarters(null);
            setSelectedQuarter(null);
        }
    };

    /**
     * Set a timer to return to the recaptcha
     * @name setTimer
     */
    const setTimer = () => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setDisplayForm(false);
            setFacetFields(null);
            setGoogleResponse(null);
            toast.dismiss();
        }, timeInMilliseconds);
    };

    /**
     * Everytime a query is submitted, log the search
     * @name postFtSearchLog
     * @param {{}} request
     * @param {bool} exportFlag
     * @async
     * @callback
     */
    const postFtSearchLog = useCallback(
        async (logObj) => {
            const { activeQuarters, exportFlag, requestParams, selectedQuarter, selectedSchoolYear, userHeaders } =
                logObj;
            const quarterId = activeQuarters.filter((obj) => obj.quarterName === selectedQuarter);
            const searchLogDto = {
                costCenterName: requestParams.costCenterName || null,
                exportFlag: exportFlag,
                fundName: requestParams.fundName || null,
                httpHeader: userHeaders,
                keyword: requestParams.searchString || null,
                paymentType: requestParams.paymentType || null,
                quarterId: quarterId[0]?.key || null,
                schoolYearId: selectedSchoolYear,
                spendCategoryName: requestParams.spendCategoryName || null,
                vendorName: requestParams.vendorName || null
            };

            try {
                setTimer();
                const options = {
                    action: "ftSearchLogCreate",
                    data: searchLogDto,
                    token
                };
                const response = await FinTransDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload) {
                        return payload;
                    }
                }
            } catch (error) {
                console.error("Error posting search log:\n", error);
            }
        },
        [token]
    );

    /**
     * Query the Financial Transparency Payments searchable service
     * @name getFtPayments
     * @callback
     * @type {(function(): void)|*}
     */
    const getFtPayments = useCallback(async () => {
        const paramRequests = {
            facetField,
            facetLimit,
            numRows,
            pageNum,
            searchString,
            sort
        };
        const requestParams = FacetedSearch.getRequestParams(params, paramRequests);
        const options = {
            action: "ftPaymentsSearchableRead",
            params: requestParams,
            token
        };
        setLoader(true);
        try {
            const response = await FinTransDao(options);
            if (response && response.data) {
                const { payload } = response.data;
                if (payload) {
                    setFacetFields(payload.facetFields);
                    setFacetPagination(payload.facetPaginationInfoDto);
                    setPayments(payload.results);
                }
                const logObj = {
                    activeQuarters,
                    exportFlag: false,
                    requestParams,
                    selectedQuarter,
                    selectedSchoolYear,
                    userHeaders
                };
                if (parseInt(pageNum, 10) === parseInt(pageNumRef.current, 10)) {
                    postFtSearchLog(logObj);
                } else {
                    pageNumRef.current = pageNum;
                }
            }
        } catch (error) {
            console.error("Error getting payments:\n", error);
        } finally {
            setLoader(false);
        }
    }, [
        activeQuarters,
        pageNum,
        params,
        postFtSearchLog,
        searchString,
        selectedQuarter,
        selectedSchoolYear,
        token,
        userHeaders
    ]);

    /**
     * Download a csv file of current search results
     * @name getFtPaymentsExport
     */
    const getFtPaymentsExport = useCallback(() => {
        const exclFieldList = "categoryName,quarterName";
        const requests = {
            exclFieldList,
            facetField,
            facetLimit,
            numRows: 50000,
            pageNum: 0,
            searchString,
            sort
        };
        const requestParams = FacetedSearch.getRequestParams(params, requests);
        const options = {
            action: "ftPaymentsExport",
            params: requestParams,
            token
        };
        setLoader(true);
        try {
            FinTransDao(options).then((response) => {
                if (response) {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "FinancialTransparency.csv");
                    document.body.appendChild(link);
                    link.click();
                    //---------------------------
                    // create a log
                    const logObj = {
                        activeQuarters,
                        exportFlag: true,
                        requestParams,
                        selectedQuarter,
                        selectedSchoolYear,
                        userHeaders
                    };
                    postFtSearchLog(logObj);
                } else {
                    toast.info("There was a problem exporting results, or there are no results to export.", {
                        autoClose: false
                    });
                }
            });
        } catch (error) {
            console.error("Error exporting results:\n", error);
        } finally {
            setLoader(false);
        }
    }, [
        activeQuarters,
        params,
        postFtSearchLog,
        searchString,
        selectedQuarter,
        selectedSchoolYear,
        token,
        userHeaders
    ]);

    /**
     * Collect a recaptcha token, validate the token, and submit the request
     * @name handleSubmit
     * @async
     * @param {{}} e
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        const recaptchaForm = e.target;
        const checkStatus = recaptchaForm.checkValidity();
        recaptchaForm.reportValidity();
        if (checkStatus) {
            if (!googleResponse || (googleResponse && !googleResponse.success)) {
                const recaptchaToken = recaptchaRef.current.getValue();
                if (recaptchaToken) {
                    const options = {
                        action: "verify",
                        data: { token: recaptchaToken }
                    };
                    // will set googleResponse and return a boolean
                    const response = await RecaptchaDao(options);
                    if (response && response.data) {
                        const { google_response } = response.data;
                        setGoogleResponse(google_response);
                    }
                }
            }
        }
    };

    /**
     * Get User Headers
     */
    useEffect(() => {
        if (userHeaders) {
            return;
        }
        /**
         * Fetch the current users headers
         * @name fetchUserHeaders
         * @async
         */
        const fetchUserHeaders = async () => {
            try {
                const options = {
                    action: "userHeadersRead"
                };
                const response = await ClientDao(options);
                if (response && response.data) {
                    const { headers, yourIpAddress } = response.data;
                    setUserHeaders(`${headers["user-agent"]} IP Address:${yourIpAddress}`);
                }
            } catch (error) {
                console.error("Error fetching client headers:\n", error);
            }
        };

        fetchUserHeaders();
    }, [userHeaders]);

    /**
     * If we have a captcha success, set a timer of 30 minutes, otherwise hide the searchable form
     */
    useEffect(() => {
        if (googleResponse && googleResponse?.success) {
            setDisplayForm(true);
            setTimer();
        } else {
            setDisplayForm(false);
            setGoogleResponse(null);
        }
    }, [googleResponse, timeInMilliseconds]);

    /**
     * If we have a valide captcha and public-token, get the current quarter
     */
    useEffect(() => {
        if (currentQuarter || !displayForm || !token) {
            return;
        }
        /**
         * Fetch the current active financial quarter
         * @name fetchCurrentQuarter
         * @async
         */
        const fetchCurrentQuarter = async () => {
            try {
                const options = {
                    action: "ftCurrentQuarterRead",
                    token
                };
                const response = await FinTransDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload) {
                        setCurrentQuarter(payload);
                    }
                }
            } catch (error) {
                console.error("Error fetching current quarter:\n", error);
            }
        };

        fetchCurrentQuarter();
    }, [currentQuarter, displayForm, token]);

    /**
     * With a successful captcha, grab all of the reportable fiscal years
     */
    useEffect(() => {
        if (reportableSchoolYears || !displayForm || !token) {
            return;
        }
        /**
         * Fetch all the active financial quarters for a given school year
         * @name fetchReportableSchoolYears
         * @async
         */
        const fetchReportableSchoolYears = async () => {
            try {
                const options = {
                    action: "ftReportableSchoolYearsRead",
                    token
                };
                const response = await FinTransDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload) {
                        payload.sort((a, b) => b.schoolYearName.localeCompare(a.schoolYearName));
                        setReportableSchoolYears(payload);
                    }
                }
            } catch (error) {
                console.error("Error fetching reportable school years:\n", error);
            }
        };

        fetchReportableSchoolYears();
    }, [displayForm, reportableSchoolYears, token]);

    /**
     * When the user selects a fiscal year, grab all the active quarters for it. Otherwise, reset.
     */
    useEffect(() => {
        if (!displayForm || !selectedSchoolYear || !token) {
            resetSearch();
            return;
        }
        if (schoolYearQuarters && schoolYearQuarters[selectedSchoolYear]) {
            setActiveQuarters(schoolYearQuarters[selectedSchoolYear]);
            schoolYearQuarters[selectedSchoolYear].map((quarter, index) => {
                if (quarter.quarterName === currentQuarter.quarterName) {
                    setSelectedQuarter(quarter.quarterName);
                }

                return index;
            });

            return;
        }
        /**
         * Fetch all the active financial quarters for a given school year
         * @name fetchQuartersBySchoolYear
         * @async
         */
        const fetchActiveQuarters = async () => {
            try {
                const options = {
                    action: "ftActiveQuartersRead",
                    schoolYearKey: selectedSchoolYear,
                    token
                };
                const response = await FinTransDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload) {
                        setActiveQuarters(payload);
                        dispatch({
                            type: "SchoolYearQuarters",
                            schoolYearQuarters: {
                                ...schoolYearQuarters,
                                [selectedSchoolYear]: payload
                            }
                        });
                        payload.map((quarter, index) => {
                            if (quarter.quarterName === currentQuarter.quarterName) {
                                setSelectedQuarter(quarter.quarterName);
                            }

                            return index;
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching active quarters:\n", error);
            }
        };

        fetchActiveQuarters();
    }, [currentQuarter, dispatch, displayForm, schoolYearQuarters, selectedSchoolYear, token]);

    /**
     * Once the user selects a quarter to report on, set it in the params object
     */
    useEffect(() => {
        if (!displayForm || !selectedQuarter || !token) {
            setFacetFields(null);
            return;
        }

        setParams((prevState) => ({
            ...prevState,
            quarterName: selectedQuarter,
            "spendCategoryName(ne)": SpendCategories.join(",")
        }));
    }, [displayForm, selectedSchoolYear, selectedQuarter, selectedSchoolYear, token]);

    /**
     * If all necessary data is present, trigger the financial payments search.
     * This effect runs when displayForm, selectedQuarter, selectedSchoolYear, token,
     * params (which includes quarterName and spendCategoryNameFilter), pageNum, or searchString change.
     */
    useEffect(() => {
        // Essential conditions for making an API call
        if (!displayForm || !selectedQuarter || !selectedSchoolYear || !token || !params.quarterName) {
            // Reset related state if conditions are not met
            setFacetFields(null);
            setPayments(null);
            // Also reset pageNumRef.current if the search effectively resets
            pageNumRef.current = 1;
            return;
        }

        // Call the API function
        getFtPayments();
    }, [displayForm, getFtPayments, pageNum, params, searchString, selectedQuarter, selectedSchoolYear, token]);

    return (
        <div style={{ display: "flex", flexDirection: "column" }}>
            <ToastContainer />
            <Header />
            <BreadCrumb label={T({ key: "Breadcrumb" })} />
            <div className="gutter-96">
                {T({ key: "Main_Instruction" })}
                <div id="public">
                    {displayForm ? (
                        <div className="gutter-95">
                            <hr />
                            {reportableSchoolYears && (
                                <SchoolYearSelect
                                    schoolYears={reportableSchoolYears}
                                    selectedSchoolYear={selectedSchoolYear}
                                    setSelectedQuarter={setSelectedQuarter}
                                    setSelectedSchoolYear={setSelectedSchoolYear}
                                />
                            )}
                            {activeQuarters && selectedSchoolYear && (
                                <QuarterSelect
                                    activeQuarters={activeQuarters}
                                    resetSearch={resetSearch}
                                    selectedQuarter={selectedQuarter}
                                    setSelectedQuarter={setSelectedQuarter}
                                />
                            )}
                            {facetFields && (
                                <FacetedSearch
                                    exportCsvHandler={getFtPaymentsExport}
                                    facetFields={facetFields}
                                    includeKeyword
                                    params={params}
                                    selectedFacets={selectedFacets}
                                    setPageNum={setPageNum}
                                    setParams={setParams}
                                    setSearchString={setSearchString}
                                    setSelectedFacets={setSelectedFacets}
                                />
                            )}
                            {payments && (
                                <PaymentsTable
                                    facetPagination={facetPagination}
                                    pageNumRef={pageNumRef}
                                    setPageNum={setPageNum}
                                    payments={payments}
                                />
                            )}
                        </div>
                    ) : (
                        <form id="recaptcha-form" onSubmit={handleSubmit}>
                            <ReCAPTCHA ref={recaptchaRef} sitekey={CAPTCHA_SITE_KEY} />
                            <hr />
                            <ActionButton label="Continue" type="submit" />
                        </form>
                    )}
                </div>
            </div>
            {loader && <LoadingSvg2 />}
        </div>
    );
};

export default Main;
